import type { Kind } from '../db/types';
import './Checkbox.css';

interface Props {
  kind: Kind;
  done?: boolean;
  /** Draws the ring in the overdue colour without filling it. */
  overdue?: boolean;
  /** A start date in the future: dashed ring, not actionable yet. */
  pending?: boolean;
  size?: number;
  onToggle?: () => void;
  label?: string;
}

/**
 * Round for tasks, square for notes — the shape alone tells you which kind of
 * row you are looking at, before you have read a word of it. A note's box is
 * never interactive; there is nothing to complete.
 */
export function Checkbox({
  kind,
  done = false,
  overdue = false,
  pending = false,
  size = 16,
  onToggle,
  label,
}: Props) {
  const style = { '--cb-size': `${size}px` } as React.CSSProperties;

  if (kind === 'note') {
    return <span className="cb cb--note" style={style} aria-hidden="true" />;
  }

  return (
    <button
      type="button"
      className={[
        'cb',
        'cb--task',
        done && 'is-done',
        overdue && !done && 'is-overdue',
        pending && !done && 'is-pending',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      role="checkbox"
      aria-checked={done}
      aria-label={label ?? (done ? 'Mark not done' : 'Mark done')}
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
    >
      {done && <span className="cb__tick">✓</span>}
    </button>
  );
}
